<template lang="pug">
div.commentary-language-chip
  VChip(
    :color="dark ? 'primary-c100' : 'primary-c700'"
    variant="outlined"
    @click="openSheet"
  )
    VIcon.mr-1 {{ mdiTranslate }}
    | {{ chipText }}
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { type Locale, useI18n } from "vue-i18n";
import { mdiCheck, mdiClose, mdiTranslate } from "@mdi/js";
import { localesByCode } from "~/i18n/locales";
import { getMergedLanguages } from "~/i18n/utils";
import { useLanguageSheetStore } from "~/stores/useLanguageSheetStore";
import { useDarkMode } from "~/composables/useDarkMode";

interface Props {
  /**
   * The codes of the selected languages.
   */
  languages: Locale[];

  /**
   * The number of available comments per language.
   */
  counts: Map<string, number>;
}

const i18n = useI18n();
const dark = useDarkMode();
const props = withDefaults(defineProps<Props>(), {});
const languageSheetStore = useLanguageSheetStore();

const openSheet = () => languageSheetStore.open({ counts: props.counts });

const chipText = computed<string>(() => {
  const localeCode = i18n.locale.value;

  const [principalCode = localeCode, ...otherLanguages] = getMergedLanguages(
    localeCode,
    props.languages,
  );
  const principalLabel =
    localesByCode.get(principalCode)?.name ?? principalCode;

  if (otherLanguages.length === 0) {
    return principalLabel;
  }
  const otherLabel = otherLanguages
    .map((code) => localesByCode.get(code)?.name ?? code)
    .join(", ");
  return `${principalLabel} (${otherLabel})`;
});
</script>
